import { logError } from '@/utils/logger';
import { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Plus, Trash, MagnifyingGlass, Buildings, CaretDown, CaretUp } from '@phosphor-icons/react';

export const OperatingCosts = ({ userRole, canEdit }) => {
  const [data, setData] = useState(null);
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [search, setSearch] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [expanded, setExpanded] = useState(true);
  const [form, setForm] = useState({ label: '', amount: '', details: '' });
  const API = process.env.REACT_APP_BACKEND_URL;

  const fetchData = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/finance/operating-costs?month=${month}`, { credentials: 'include' });
      if (res.ok) setData(await res.json());
    } catch (error) { logError('Fetch opex:', error); }
  }, [API, month]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const res = await fetch(`${API}/api/finance/operating-costs`, {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, amount: parseFloat(form.amount) || 0, month }),
      });
      if (res.ok) {
        toast.success('Frais ajouté');
        setForm({ label: '', amount: '', details: '' });
        setShowForm(false);
        fetchData();
      } else { const err = await res.json(); toast.error(err.detail || 'Erreur'); }
    } catch { toast.error('Erreur'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer ce frais ?')) return;
    try {
      const res = await fetch(`${API}/api/finance/operating-costs/${id}`, { method: 'DELETE', credentials: 'include' });
      if (res.ok) { toast.success('Supprimé'); fetchData(); }
    } catch { toast.error('Erreur'); }
  };

  if (!data) return null;
  const filteredExpenses = (data.expenses || []).filter(e => !search || e.label?.toLowerCase().includes(search.toLowerCase()) || e.details?.toLowerCase().includes(search.toLowerCase()));

  return (
    <div className="bg-[#0F172A] border border-[#1E293B] rounded-md" data-testid="operating-costs-section">
      <button onClick={() => setExpanded(!expanded)} className="w-full px-4 sm:px-6 py-4 flex items-center justify-between text-left">
        <div className="flex items-center gap-3">
          <Buildings size={22} weight="duotone" className="text-[#8B5CF6]" />
          <div>
            <h2 className="text-lg font-bold text-[#F8FAFC]">Frais de fonctionnement mensuels</h2>
            <p className="text-xs text-[#94A3B8]">Salaires automatiques + frais et indemnités</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-[#8B5CF6] font-bold text-lg hidden sm:block">{data.grand_total?.toFixed(2)} $</span>
          {expanded ? <CaretUp size={16} className="text-[#64748B]" /> : <CaretDown size={16} className="text-[#64748B]" />}
        </div>
      </button>

      {expanded && (
        <div className="px-4 sm:px-6 pb-6 space-y-4">
          <div className="flex flex-col sm:flex-row gap-3">
            <Input type="month" value={month} onChange={e => setMonth(e.target.value)} className="bg-[#1E293B] border-[#334155] text-[#F8FAFC] sm:w-44" data-testid="opex-month-select" />
            <div className="relative flex-1">
              <MagnifyingGlass size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#64748B]" />
              <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Rechercher un frais..." className="bg-[#1E293B] border-[#334155] text-[#F8FAFC] pl-9" data-testid="opex-search" />
            </div>
            {canEdit && (
              <Button onClick={() => setShowForm(!showForm)} className="bg-[#8B5CF6] hover:bg-[#7C3AED] text-white" data-testid="opex-add-button">
                <Plus size={16} className="mr-1.5" /> Ajouter un frais
              </Button>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="bg-[#1E293B]/40 border border-[#334155]/50 rounded-md p-4">
              <p className="text-[10px] uppercase tracking-wider text-[#64748B]">Salaires (automatique)</p>
              <p className="text-xl font-bold text-[#10B981] mt-1" data-testid="opex-salaries">{data.total_salaries?.toFixed(2)} $</p>
              <p className="text-xs text-[#64748B] mt-0.5">{data.paid_agents}/{data.total_agents} agents avec salaire renseigné</p>
            </div>
            <div className="bg-[#1E293B]/40 border border-[#334155]/50 rounded-md p-4">
              <p className="text-[10px] uppercase tracking-wider text-[#64748B]">Frais & indemnités ({data.expenses?.length || 0})</p>
              <p className="text-xl font-bold text-[#F59E0B] mt-1" data-testid="opex-expenses">{data.total_expenses?.toFixed(2)} $</p>
            </div>
            <div className="bg-[#8B5CF6]/10 border border-[#8B5CF6]/30 rounded-md p-4">
              <p className="text-[10px] uppercase tracking-wider text-[#8B5CF6]">Total du mois</p>
              <p className="text-xl font-bold text-[#F8FAFC] mt-1" data-testid="opex-total">{data.grand_total?.toFixed(2)} $</p>
            </div>
          </div>

          {showForm && (
            <form onSubmit={handleSubmit} className="space-y-3 p-4 bg-[#1E293B]/30 rounded-md border border-[#334155]">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-[#F8FAFC] text-xs">Libellé du frais *</Label>
                  <Input value={form.label} onChange={e => setForm({ ...form, label: e.target.value })} required placeholder="Ex: Indemnité transport, Loyer bureau..." className="bg-[#1E293B] border-[#334155] text-[#F8FAFC]" data-testid="opex-label" />
                </div>
                <div>
                  <Label className="text-[#F8FAFC] text-xs">Montant ($) *</Label>
                  <Input type="number" min="0" step="0.01" value={form.amount} onChange={e => setForm({ ...form, amount: e.target.value })} required className="bg-[#1E293B] border-[#334155] text-[#F8FAFC]" data-testid="opex-amount" />
                </div>
              </div>
              <div>
                <Label className="text-[#F8FAFC] text-xs">Détails — comment l'argent a été dépensé</Label>
                <Textarea value={form.details} onChange={e => setForm({ ...form, details: e.target.value })} rows={2} placeholder="Détaillez la dépense..." className="bg-[#1E293B] border-[#334155] text-[#F8FAFC]" data-testid="opex-details" />
              </div>
              <div className="flex justify-end">
                <Button type="submit" className="bg-[#10B981] hover:bg-[#059669] text-white h-9" data-testid="opex-save-button">Enregistrer</Button>
              </div>
            </form>
          )}

          <div className="space-y-2">
            {filteredExpenses.length === 0 ? (
              <p className="text-center text-[#64748B] text-sm py-3">Aucun frais manuel pour ce mois</p>
            ) : filteredExpenses.map(exp => (
              <div key={exp.expense_id} className="flex items-start justify-between gap-3 p-3 bg-[#1E293B]/30 rounded-md border border-[#334155]/50" data-testid={`opex-${exp.expense_id}`}>
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="text-sm font-medium text-[#F8FAFC]">{exp.label}</p>
                    <span className="text-sm font-bold text-[#F59E0B]">{exp.amount?.toFixed(2)} $</span>
                  </div>
                  {exp.details && <p className="text-xs text-[#94A3B8] mt-1">{exp.details}</p>}
                  {exp.created_by_name && <p className="text-[10px] text-[#64748B] mt-0.5">Par {exp.created_by_name}</p>}
                </div>
                {canEdit && (
                  <Button size="sm" variant="ghost" onClick={() => handleDelete(exp.expense_id)} className="text-[#EF4444] hover:text-white hover:bg-[#EF4444] h-7 w-7 p-0 flex-shrink-0"><Trash size={14} /></Button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default OperatingCosts;
